/**
 * Phase 5.1 — Due Out edit / authorization / audit workflow.
 *
 * Doctrine:
 *   - Bucket 4.5 §4.5.1 Due Out is a hard customer-promise constraint.
 *   - Bucket 4.5 §4.5.5 Due Out outranks Waiter Flex.
 *   - Bucket 4.5 §4.5.7 Changing Due Out is NOT a normal scheduling mutation;
 *     it requires authorization, reason, and audit.
 *   - Bucket 4.5 §4.5.8 Re-promise vs Breach Record vs Correction must be
 *     distinguishable in the audit row.
 *
 * Why this module exists
 * ----------------------
 * Today the only Due Out edit path (`EditTicketDetailsModal.tsx:120-124`)
 * writes `tickets.due_out` through `mutateTicketSchedule` as a companion
 * field and emits a generic `ticket_date_changed` OCIS event. That event:
 *   - carries only the NEW value (no old → new pair),
 *   - does not require or capture a reason,
 *   - does not classify the change (correction / re-promise / breach),
 *   - is gated only by `canEditTickets` (no Due-Out-specific role guard),
 *   - never records customer communication / approval.
 *
 * This module provides the canonical Due Out edit vocabulary so the next
 * slice (Phase 5.2 — mutation-path-wide validation) and the UI layer can
 * read one source of truth.
 *
 * Phase 5.1 scope: HELPER + TESTS ONLY.
 *   - Pure. No DB I/O, no clock, no React hooks. Caller passes `now`.
 *   - No DB migration. The existing `audit_log` and `ocis_events` tables
 *     accept arbitrary JSON; the audit payload defined here is JSON-shaped
 *     and lands in those columns when Phase 5.2 wires it in.
 *   - No runtime mutation-path changes. `EditTicketDetailsModal`,
 *     `mutateTicketSchedule`, `DueOutMitigationModal`, cascade, AutoBump,
 *     OptiAssign — all unchanged.
 *   - Bucket 4.5 §4.5.5 precedence preserved; this module does NOT touch
 *     `RESOLUTION_PRIORITY`.
 *
 * Adoption points are documented at the bottom of this file.
 */

// =============================================================================
// Type model
// =============================================================================

/**
 * Canonical Due Out edit reasons. Captured in the audit payload so the
 * change is explainable to managers, customers, and auditors.
 *
 *   - 'customer_request'          — customer asked for the change
 *   - 'parts_delay'               — parts ETA pushed completion
 *   - 'scope_expansion'           — added work expanded the job
 *   - 'tech_unavailable'          — tech absence / capacity change
 *   - 'shop_capacity'             — shop-wide capacity constraint
 *   - 'data_entry_correction'     — original Due Out value was wrong
 *   - 'breach_recorded'           — recording a breach after the fact
 *   - 'system_recommendation'     — Catalyst / OptiBay suggestion accepted
 *   - 'other'                     — free-text required in `reasonText`
 */
export type DueOutEditReason =
  | 'customer_request'
  | 'parts_delay'
  | 'scope_expansion'
  | 'tech_unavailable'
  | 'shop_capacity'
  | 'data_entry_correction'
  | 'breach_recorded'
  | 'system_recommendation'
  | 'other';

/** Frozen list of all reasons; useful for UI dropdowns + tests. */
export const DUE_OUT_EDIT_REASONS = Object.freeze([
  'customer_request',
  'parts_delay',
  'scope_expansion',
  'tech_unavailable',
  'shop_capacity',
  'data_entry_correction',
  'breach_recorded',
  'system_recommendation',
  'other',
] as const) satisfies ReadonlyArray<DueOutEditReason>;

/**
 * Classification of the Due Out edit.
 *
 *   - 'setting'        — `null → value` (initial promise set).
 *   - 'clearing'       — `value → null` (rare; removes a promise).
 *   - 're_promise'     — new > old. Extending the promised time. Bucket
 *                        4.5 §4.5.8 explicit re-promise category.
 *   - 'shortening'     — new < old AND old has not yet passed `now`.
 *                        Bringing the promise EARLIER (still in future).
 *   - 'correction'     — adjustment to a Due Out whose old value has
 *                        already passed `now` (data-entry fix). Default
 *                        for the past-due case unless explicitly marked
 *                        as a breach record.
 *   - 'breach_record'  — explicit acknowledgment of an already-breached
 *                        Due Out. Caller-supplied intent.
 */
export type DueOutEditKind =
  | 'setting'
  | 'clearing'
  | 're_promise'
  | 'shortening'
  | 'correction'
  | 'breach_record';

export const DUE_OUT_EDIT_KINDS = Object.freeze([
  'setting',
  'clearing',
  're_promise',
  'shortening',
  'correction',
  'breach_record',
] as const) satisfies ReadonlyArray<DueOutEditKind>;

/**
 * Roles authorized to edit Due Out by default. Mirrors the master fix
 * list: Service Writer, Manager, Admin, Owner. Shop-configured roles
 * can be added via `DueOutEditShopConfig.additionalAuthorizedRoles`.
 */
export const DUE_OUT_EDIT_AUTHORIZED_ROLES = Object.freeze([
  'OWNER',
  'MANAGER',
  'SERVICE_WRITER',
  'ORG_ADMIN',
] as const);

export type DueOutEditAuthorizedRole = typeof DUE_OUT_EDIT_AUTHORIZED_ROLES[number];

/** Optional shop-side override of the authorized-role list. */
export interface DueOutEditShopConfig {
  /**
   * Shop-configured additional roles allowed to edit Due Out. Merged
   * with `DUE_OUT_EDIT_AUTHORIZED_ROLES` at authorization time.
   *
   * Future Phase 5.2/5.3 may surface this via `shop_culture` settings.
   */
  additionalAuthorizedRoles?: ReadonlyArray<string>;
}

// =============================================================================
// Internal helpers
// =============================================================================

/** Parse a Date / ISO string into ms; null on bad input. */
function parseTs(v: Date | string | null | undefined): number | null {
  if (v == null) return null;
  if (v instanceof Date) {
    const ms = v.getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  const ms = Date.parse(String(v));
  return Number.isFinite(ms) ? ms : null;
}

/** Convert a Date / ISO string to a normalized ISO string; null on bad input. */
function toIso(v: Date | string | null | undefined): string | null {
  const ms = parseTs(v);
  if (ms == null) return null;
  return new Date(ms).toISOString();
}

// =============================================================================
// Public API
// =============================================================================

/**
 * Role gate for Due Out edits.
 *
 * Returns true iff `role` is in the default authorized set OR in the
 * shop's optional additional-roles list.
 *
 * Pure: does not read auth state, does not require React context. The
 * caller resolves the role from `usePermissions.role` (or equivalent)
 * before calling.
 */
export function canEditDueOut(
  role: string | null | undefined,
  shopConfig?: DueOutEditShopConfig,
): boolean {
  if (!role) return false;
  const upper = String(role).toUpperCase();
  if ((DUE_OUT_EDIT_AUTHORIZED_ROLES as readonly string[]).includes(upper)) return true;
  const extras = shopConfig?.additionalAuthorizedRoles;
  if (!extras) return false;
  return extras.some(r => String(r).toUpperCase() === upper);
}

export interface DueOutEditClassificationInput {
  oldDueOut: Date | string | null | undefined;
  newDueOut: Date | string | null | undefined;
  /** Reference clock for past-due determination. */
  now: Date;
  /**
   * When the past-due case is ambiguous, the caller specifies whether
   * the edit is a `'breach_record'` (acknowledging the breach) or a
   * `'correction'` (data-entry fix). Default is `'correction'`.
   */
  intent?: 'correction' | 'breach_record';
}

/**
 * Classify a Due Out edit into one of the six `DueOutEditKind` values.
 *
 * Resolution order:
 *   1. Both `null` → `'correction'` (safe no-op label).
 *   2. `null → value`              → `'setting'`.
 *   3. `value → null`              → `'clearing'`.
 *   4. `new > old`                  → `'re_promise'`.
 *   5. `new < old` AND `old < now`  → `'breach_record'` if intent says so,
 *      else `'correction'`.
 *   6. `new < old` AND `old >= now` → `'shortening'`.
 *   7. `new == old`                 → `'correction'` (no-op).
 */
export function classifyDueOutEdit(
  input: DueOutEditClassificationInput,
): DueOutEditKind {
  const oldMs = parseTs(input.oldDueOut);
  const newMs = parseTs(input.newDueOut);
  if (oldMs == null && newMs == null) return 'correction';
  if (oldMs == null && newMs != null) return 'setting';
  if (oldMs != null && newMs == null) return 'clearing';
  // Both present.
  const o = oldMs as number;
  const n = newMs as number;
  if (n > o) return 're_promise';
  if (n < o) {
    const nowMs = input.now.getTime();
    if (Number.isFinite(nowMs) && o < nowMs) {
      return input.intent === 'breach_record' ? 'breach_record' : 'correction';
    }
    return 'shortening';
  }
  return 'correction';
}

/**
 * Does this Due Out edit require an explicit reason?
 *
 * Returns true for every non-no-op edit. A "no-op" is either both null,
 * or both set to the exact same instant. Anything else — including
 * `setting`, `clearing`, `re_promise`, `shortening`, `correction`,
 * `breach_record` — requires a reason in the audit payload.
 */
export function requiresDueOutEditReason(
  oldDueOut: Date | string | null | undefined,
  newDueOut: Date | string | null | undefined,
): boolean {
  const oldMs = parseTs(oldDueOut);
  const newMs = parseTs(newDueOut);
  if (oldMs == null && newMs == null) return false;
  if (oldMs != null && newMs != null && oldMs === newMs) return false;
  return true;
}

/** Discrete validation error codes (machine-readable). */
export type DueOutEditValidationError =
  | 'unauthorized_role'
  | 'missing_actor'
  | 'missing_reason'
  | 'missing_reason_text'
  | 'invalid_new_due_out'
  | 'no_change';

export interface DueOutEditValidationInput {
  oldDueOut: Date | string | null | undefined;
  newDueOut: Date | string | null | undefined;
  now: Date;
  role: string | null | undefined;
  shopConfig?: DueOutEditShopConfig;
  reason?: DueOutEditReason | null;
  /** Required when `reason === 'other'`. */
  reasonText?: string | null;
  actorUserId?: string | null;
  intent?: 'correction' | 'breach_record';
}

export interface DueOutEditValidationResult {
  ok: boolean;
  kind: DueOutEditKind;
  errors: DueOutEditValidationError[];
}

/**
 * Validate a proposed Due Out edit. Returns `{ ok, kind, errors }`.
 *
 * Errors are collected (not short-circuited) so UI can render all
 * problems at once. `ok === true` iff `errors` is empty.
 *
 * Pure: same inputs → same output. Does not write anything.
 */
export function validateDueOutEdit(
  input: DueOutEditValidationInput,
): DueOutEditValidationResult {
  const errors: DueOutEditValidationError[] = [];

  // Role gate.
  if (!canEditDueOut(input.role, input.shopConfig)) {
    errors.push('unauthorized_role');
  }

  // Actor.
  if (!input.actorUserId || String(input.actorUserId).trim().length === 0) {
    errors.push('missing_actor');
  }

  // Invalid new due out (non-null but unparseable).
  if (input.newDueOut != null && parseTs(input.newDueOut) == null) {
    errors.push('invalid_new_due_out');
  }

  // Change/no-change + reason gate.
  const isChange = requiresDueOutEditReason(input.oldDueOut, input.newDueOut);
  if (!isChange) {
    errors.push('no_change');
  } else {
    if (!input.reason) {
      errors.push('missing_reason');
    } else if (
      input.reason === 'other' &&
      (!input.reasonText || String(input.reasonText).trim().length === 0)
    ) {
      errors.push('missing_reason_text');
    }
  }

  const kind = classifyDueOutEdit({
    oldDueOut: input.oldDueOut,
    newDueOut: input.newDueOut,
    now: input.now,
    intent: input.intent,
  });

  return { ok: errors.length === 0, kind, errors };
}

/**
 * Canonical audit payload shape for a Due Out edit.
 *
 * Lands as the `payload` JSON on the corresponding OCIS event AND/OR as
 * `audit_log.new_value` when Phase 5.2 wires it in. Stable
 * snake_case fields so downstream consumers (UI, reports, exports) can
 * parse without re-deriving.
 *
 * Required identity / change pair:
 *   - `ticket_id`, `old_due_out`, `new_due_out`, `kind`.
 *
 * Required who/why/when:
 *   - `actor_user_id`, `actor_role`, `reason`, `edited_at`.
 *
 * Optional customer-communication block (for re-promise / shortening):
 *   - `customer_communication_approved`, `customer_communication_note`.
 *
 * `reason_text` is required when `reason === 'other'`.
 */
export interface DueOutEditAuditPayload {
  ticket_id: string;
  old_due_out: string | null;
  new_due_out: string | null;
  kind: DueOutEditKind;
  reason: DueOutEditReason;
  reason_text: string | null;
  actor_user_id: string;
  actor_role: string;
  edited_at: string;
  customer_communication_approved: boolean | null;
  customer_communication_note: string | null;
}

export interface BuildDueOutEditAuditPayloadInput {
  ticketId: string;
  oldDueOut: Date | string | null | undefined;
  newDueOut: Date | string | null | undefined;
  now: Date;
  actorUserId: string;
  role: string;
  reason: DueOutEditReason;
  reasonText?: string | null;
  intent?: 'correction' | 'breach_record';
  customerCommunicationApproved?: boolean | null;
  customerCommunicationNote?: string | null;
  /** Override the audit row timestamp. Defaults to `now`. */
  editedAt?: Date;
}

/**
 * Build the canonical audit payload for a Due Out edit. Does NOT validate
 * authorization — call `validateDueOutEdit` first if the caller needs
 * authorization enforcement. This helper exists separately so a system
 * actor (e.g., a Catalyst auto-resolver in Phase 4.2's adoption path)
 * can build the payload with `role: 'SYSTEM'` independently of the
 * user-role gate.
 *
 * `actor_role` is preserved in the original casing the caller passes
 * (e.g., 'SERVICE_WRITER' vs 'service_writer'). The role-gate logic
 * upper-cases for comparison but the audit row should reflect the
 * literal role string the caller used.
 */
export function buildDueOutEditAuditPayload(
  input: BuildDueOutEditAuditPayloadInput,
): DueOutEditAuditPayload {
  const kind = classifyDueOutEdit({
    oldDueOut: input.oldDueOut,
    newDueOut: input.newDueOut,
    now: input.now,
    intent: input.intent,
  });
  const editedAt = input.editedAt ?? input.now;
  return {
    ticket_id: input.ticketId,
    old_due_out: toIso(input.oldDueOut),
    new_due_out: toIso(input.newDueOut),
    kind,
    reason: input.reason,
    reason_text:
      input.reasonText != null && String(input.reasonText).trim().length > 0
        ? String(input.reasonText)
        : null,
    actor_user_id: input.actorUserId,
    actor_role: input.role,
    edited_at: editedAt.toISOString(),
    customer_communication_approved:
      input.customerCommunicationApproved ?? null,
    customer_communication_note:
      input.customerCommunicationNote != null &&
      String(input.customerCommunicationNote).trim().length > 0
        ? String(input.customerCommunicationNote)
        : null,
  };
}

// =============================================================================
// Phase 5.3 — Audit / OCIS vocabulary constants
// =============================================================================
//
// These constants give callers (UI, audit-log writer, OCIS ingest) one
// canonical place to look up the formal name of a Due Out edit event.
// They are SAFE to use today:
//
//   - `DUE_OUT_EDIT_AUDIT_LOG_ACTION` — the canonical SCREAMING_SNAKE
//     `AdminActionType` reserved for `audit_log.action`. The column is
//     `TEXT NOT NULL` (no DB enum), so this value can be written today.
//     No writer emits it yet; Phase 5.4+ will wire one through
//     `logAdminAction`.
//   - `DUE_OUT_EDIT_OCIS_EVENT_TYPE_DEFERRED` — the FUTURE canonical
//     `ocis_event_type` enum value for a Due Out edit. The DB column is
//     constrained by the `ocis_event_type` PG enum (defined in migration
//     20260130154936), so adding `'due_out_changed'` requires a
//     migration. UNTIL THAT MIGRATION LANDS, callers MUST continue to
//     emit `OCIS_EVENT_TYPE_BACKWARD_COMPAT` with the canonical payload
//     under `payload.due_out_edit`.
//   - `OCIS_EVENT_TYPE_BACKWARD_COMPAT` — the current event_type to use
//     until the enum migration ships. Defined as a constant so any
//     drift between callers gets caught by source-scan tests.
//   - `OCIS_PAYLOAD_DUE_OUT_EDIT_KEY` — the canonical payload field
//     that carries the `DueOutEditAuditPayload` JSON.

/**
 * Canonical `audit_log.action` value for a Due Out edit. Safe to use
 * today (`audit_log.action` is `TEXT NOT NULL`, no DB enum). The
 * canonical AdminActionType union in `supabase/functions/_shared/
 * auditLog.ts` reserves the SCREAMING_SNAKE name `'DUE_OUT_CHANGED'`.
 */
export const DUE_OUT_EDIT_AUDIT_LOG_ACTION = 'DUE_OUT_CHANGED' as const;
export type DueOutEditAuditLogAction = typeof DUE_OUT_EDIT_AUDIT_LOG_ACTION;

/**
 * FUTURE canonical `ocis_event_type` value for a Due Out edit, deferred
 * pending DB enum migration. Callers MUST NOT emit this value today —
 * the `ocis_events.event_type` DB enum (PG type `ocis_event_type`,
 * migration 20260130154936) does not include it. Imported as a
 * constant so source-scan tests can verify no caller has slipped past
 * the deferral.
 */
export const DUE_OUT_EDIT_OCIS_EVENT_TYPE_DEFERRED = 'due_out_changed' as const;

/**
 * Backward-compatible `ocis_event_type` value to use for Due Out edits
 * until the DB enum migration ships. Existing timeline consumers
 * already handle this value; the Phase 5.2 contract puts the canonical
 * `DueOutEditAuditPayload` under `payload.due_out_edit`.
 */
export const OCIS_EVENT_TYPE_BACKWARD_COMPAT = 'ticket_date_changed' as const;

/**
 * Canonical payload field name that carries the `DueOutEditAuditPayload`
 * JSON inside an OCIS event of type `OCIS_EVENT_TYPE_BACKWARD_COMPAT`.
 * Source-scan tests verify all callers spell it the same way.
 */
export const OCIS_PAYLOAD_DUE_OUT_EDIT_KEY = 'due_out_edit' as const;

// =============================================================================
// Phase 5.2 — Mutation-path companion-field gate
// =============================================================================
//
// `mutateTicketSchedule` accepts `companionFields.due_out` as a non-allowlisted
// atomic write. Phase 5.2 wires that companion-field path through an
// authorization/audit gate: if `due_out` is being changed, a valid
// `DueOutEditAuditContext` must be supplied; otherwise the mutation fails
// closed.
//
// The gate is a pure function so the mutation helper can be tested without
// a Supabase mock. Callers compose:
//
//   const gate = gateDueOutCompanionField({
//     companionFields,
//     oldDueOut: currentDueOutFromTicket,
//     auditContext: options.dueOutEditAuditContext,
//     ticketId,
//     now: new Date(),
//   });
//   if (!gate.ok) return { success: false, error: ... };
//   const auditPayload = gate.auditPayload;  // attach to OCIS / audit_log

/** Audit context the mutation layer must receive for a Due Out change. */
export interface DueOutEditAuditContext {
  /**
   * The ticket's CURRENT `due_out` value (before the proposed change).
   * Caller-supplied so the gate doesn't need an extra DB round-trip on
   * the `skipCascade: true` fast path. Pass the value the caller loaded
   * for the form / mutation.
   */
  oldDueOut: Date | string | null | undefined;
  /** Acting user id (UUID). */
  actorUserId: string;
  /** Acting user role at edit time (e.g., 'OWNER'). */
  role: string;
  /** Phase 5.1 reason code. Required when there is an actual change. */
  reason: DueOutEditReason;
  /** Free-text reason when `reason === 'other'`. */
  reasonText?: string | null;
  /** Optional shop-side authorized-role override. */
  shopConfig?: DueOutEditShopConfig;
  /** Disambiguate past-due cases (defaults to `'correction'`). */
  intent?: 'correction' | 'breach_record';
  /** Optional customer-comm acknowledgement. */
  customerCommunicationApproved?: boolean | null;
  /** Optional free-text note about customer communication. */
  customerCommunicationNote?: string | null;
}

export interface GateDueOutCompanionFieldInput {
  /** The companion-fields bag passed to the mutation helper. */
  companionFields: Record<string, unknown> | null | undefined;
  /** Caller-supplied audit context. May be undefined when no Due Out edit is requested. */
  auditContext: DueOutEditAuditContext | null | undefined;
  /** Ticket id (used for the resulting audit payload). */
  ticketId: string;
  /** Reference clock for past-due classification. */
  now: Date;
}

/** Gate error codes (machine-readable). */
export type DueOutCompanionGateError =
  | 'missing_audit_context'
  | DueOutEditValidationError;

export interface GateDueOutCompanionFieldResult {
  /** True iff the gate allows the mutation to proceed. */
  ok: boolean;
  /** Did `companion.due_out` actually differ from `auditContext.oldDueOut`? */
  changed: boolean;
  /** Classification of the proposed edit (present when changed). */
  kind?: DueOutEditKind;
  /** Validation errors. Empty when `ok === true`. */
  errors: DueOutCompanionGateError[];
  /** Built audit payload, present when ok=true AND changed=true. */
  auditPayload?: DueOutEditAuditPayload;
}

/**
 * Phase 5.2 companion-field gate. Decides whether a proposed
 * `mutateTicketSchedule` call carrying `companionFields.due_out` is
 * authorized and produces the canonical audit payload.
 *
 * Behavior:
 *   - `companion.due_out` is ABSENT (no `due_out` key in the bag) →
 *     `{ ok: true, changed: false }`. Mutation proceeds without any
 *     Due Out audit overhead.
 *   - `companion.due_out` is PRESENT but equal to `oldDueOut` (both null
 *     or same ISO instant) → `{ ok: true, changed: false }`. Mutation
 *     proceeds without an audit payload — there is no real change.
 *   - `companion.due_out` is PRESENT and changed:
 *       * `auditContext == null` → `{ ok: false, errors: ['missing_audit_context'] }`.
 *       * `validateDueOutEdit` returns errors → `{ ok: false, errors: [...] }`.
 *       * otherwise → `{ ok: true, changed: true, kind, auditPayload }`.
 *
 * Pure: no I/O, no clock reads, no DB calls.
 */
export function gateDueOutCompanionField(
  input: GateDueOutCompanionFieldInput,
): GateDueOutCompanionFieldResult {
  const companion = input.companionFields ?? {};
  if (!Object.prototype.hasOwnProperty.call(companion, 'due_out')) {
    return { ok: true, changed: false, errors: [] };
  }
  const newDueOut = companion['due_out'] as Date | string | null | undefined;
  const oldDueOut = input.auditContext?.oldDueOut;

  // No actual change → pass through silently.
  if (!requiresDueOutEditReason(oldDueOut, newDueOut)) {
    return { ok: true, changed: false, errors: [] };
  }

  if (!input.auditContext) {
    return { ok: false, changed: true, errors: ['missing_audit_context'] };
  }

  const validation = validateDueOutEdit({
    oldDueOut,
    newDueOut,
    now: input.now,
    role: input.auditContext.role,
    reason: input.auditContext.reason,
    reasonText: input.auditContext.reasonText,
    actorUserId: input.auditContext.actorUserId,
    shopConfig: input.auditContext.shopConfig,
    intent: input.auditContext.intent,
  });

  if (!validation.ok) {
    return {
      ok: false,
      changed: true,
      kind: validation.kind,
      errors: validation.errors as DueOutCompanionGateError[],
    };
  }

  const auditPayload = buildDueOutEditAuditPayload({
    ticketId: input.ticketId,
    oldDueOut,
    newDueOut,
    now: input.now,
    actorUserId: input.auditContext.actorUserId,
    role: input.auditContext.role,
    reason: input.auditContext.reason,
    reasonText: input.auditContext.reasonText,
    intent: input.auditContext.intent,
    customerCommunicationApproved: input.auditContext.customerCommunicationApproved,
    customerCommunicationNote: input.auditContext.customerCommunicationNote,
  });

  return {
    ok: true,
    changed: true,
    kind: validation.kind,
    errors: [],
    auditPayload,
  };
}

// =============================================================================
// Adoption points (documented in code; no behavior change in this slice)
// =============================================================================
//
// 1. EditTicketDetailsModal (smallest next step):
//    Before calling `mutateTicketSchedule` with `companionFields.due_out`,
//    call `validateDueOutEdit(...)`. If `!ok`, surface errors[]; if `ok`,
//    proceed AND replace the generic `ticket_date_changed` OCIS event
//    with one carrying `buildDueOutEditAuditPayload(...)` as `payload`.
//    The OCIS event_type can stay `ticket_date_changed` (or a future
//    `due_out_changed` enum value once schema lands).
//
// 2. mutateTicketSchedule companion-field gate:
//    Phase 5.2 candidate — when the companion `due_out` value differs
//    from the row's current value, require the audit payload to be
//    attached to the mutation context. Reject mutations that touch
//    `due_out` without a Phase 5.1 audit payload.
//
// 3. Catalyst auto-resolution adoption (Phase 4.2 connector):
//    A future Catalyst resolver that programmatically extends Due Out
//    must call `buildDueOutEditAuditPayload({ role: 'SYSTEM', reason:
//    'system_recommendation', ... })` so the audit row carries the same
//    shape and the manager review surface treats it uniformly.
//
// 4. Audit-log writer:
//    Add a `DUE_OUT_CHANGED` canonical `AdminActionType` and route
//    Phase 5.2 writers through `logAdminAction` per the V-E-1 audit
//    contract. The Phase 5.1 payload becomes the `new_value` JSON on
//    that row; `old_value` carries `{ old_due_out, ticket_id }` for
//    point-in-time reads.
//
// 5. UI surface (manager review):
//    A future managerial surface ("Due Out Changes Log") consumes
//    `DueOutEditAuditPayload[]` and groups by `kind`. Breach records
//    are surfaced separately for KPI rollups.
